import { ref, computed, watch, onMounted, unref } from 'vue'

export interface NumberStepperProps {
    min: number | null
    max: number | null
    step: number
    default: number | string | null
}

// The vueform element context the stepper drives (value/model/active are refs; update writes the model).
export interface NumberStepperElementCtx {
    value: any
    model: any
    active: any
    update: (n: number | null) => void
}

// Display/stepper logic for the custom number-stepper, extracted so it can be unit-tested without
// standing up a full vueform form.
export function useNumberStepper(element: NumberStepperElementCtx, props: NumberStepperProps) {
    const toNum = (m: any): number | null => {
        const n = Number(m)
        return Number.isFinite(n) ? n : null
    }

    // Unwrap: element.value/model are refs in setup — read raw they're the never-null ref object, pinning the default.
    const external = () => {
        const v = unref(element.value)
        return v != null ? v : unref(element.model)
    }
    const value = ref<number | null>(toNum(external()))
    watch(external, (m) => {
        const n = toNum(m)
        if (n !== value.value) value.value = n
    })

    const clamp = (n: number) => {
        if (props.min != null && n < props.min) return props.min
        if (props.max != null && n > props.max) return props.max
        return n
    }

    const commit = (n: number | null) => {
        value.value = n
        if (unref(element.model) !== n) element.update(n)
    }

    const base = () => (value.value ?? props.min ?? 0)
    const atMin = computed(() => props.min != null && base() <= props.min)
    const atMax = computed(() => props.max != null && base() >= props.max)

    // From empty, the first step lands on the floor (min, else 0), not min+step.
    const stepBy = (dir: number) => {
        if (value.value == null) { commit(clamp(props.min ?? 0)); return }
        commit(clamp(value.value + dir * (props.step || 1)))
    }

    const onInput = (e: Event) => {
        const raw = (e.target as HTMLInputElement).value.trim()
        if (raw === '') { commit(null); return }
        const n = Number(raw)
        commit(Number.isFinite(n) ? n : null)
    }

    // Snap a typed value back into range once the field is left.
    const onBlur = () => {
        if (value.value == null) return
        commit(clamp(value.value))
    }

    // Seed the schema default when active and either empty or out of range, so a restored in-range value wins.
    const seedDefault = () => {
        if (!unref(element.active) || props.default == null) return
        const cur = toNum(external())
        const outOfRange = cur != null &&
            ((props.min != null && cur < props.min) || (props.max != null && cur > props.max))
        if (cur == null || outOfRange) commit(clamp(toNum(props.default) as number))
    }
    onMounted(seedDefault)
    watch(() => unref(element.active), (active) => { if (active) seedDefault() })

    return { value, atMin, atMax, stepBy, onInput, onBlur }
}
